import { AdminAccount, AdminRole } from '../types';

export type Permission =
  | 'dashboard'
  | 'userManagement'
  | 'transactionManagement'
  | 'withdrawalManagement'
  | 'manualGameDepositManagement'
  | 'ticketManagement'
  | 'adminManagement'
  | 'agentManagement'
  | 'agentPopupPermissionManagement'
  | 'walletManagement'
  | 'gameProviderManagement'
  | 'vfxConfigManagement'
  | 'transactionLimitsManagement'
  | 'domainManagement'
  | 'cashSettlementManagement'
  | 'cashSettlementVoid'
  | 'reportManagement';

export const ALL_PERMISSIONS: Permission[] = [
  'dashboard',
  'userManagement',
  'transactionManagement',
  'withdrawalManagement',
  'manualGameDepositManagement',
  'ticketManagement',
  'adminManagement',
  'agentManagement',
  'agentPopupPermissionManagement',
  'walletManagement',
  'gameProviderManagement',
  'vfxConfigManagement',
  'transactionLimitsManagement',
  'domainManagement',
  'cashSettlementManagement',
  'cashSettlementVoid',
  'reportManagement',
];

// Codes that exist only as frontend UI concepts — no backend route is gated by them and
// they are intentionally NOT rows in the backend `permissions` catalog table
// (see kioskgaming_backend/src/config/defaultRolePermissions.js). They must be excluded
// from any payload sent to the backend (setPermissions/grantPermissions), otherwise the
// backend rejects the whole call with "Invalid permission codes".
export const FRONTEND_ONLY_PERMISSIONS: Permission[] = [
  'dashboard',
  'manualGameDepositManagement',
  'transactionLimitsManagement',
  'domainManagement',
  'vfxConfigManagement',
];

// Strip frontend-only codes so the remaining list matches the backend permission catalog.
export const toBackendPermissionCodes = (codes: Permission[]): string[] =>
  codes.filter((code) => !FRONTEND_ONLY_PERMISSIONS.includes(code));

// Human-readable labels for permission codes. UI must show these instead of the raw
// variable-style code (e.g. "Wallet Management" not "walletManagement") so non-technical
// admins can read the role summary. Covers frontend-only codes too, which are not in the
// backend permission catalog and therefore have no `name` from the API.
export const PERMISSION_LABELS: Record<Permission, string> = {
  dashboard: 'Dashboard',
  userManagement: 'User Management',
  transactionManagement: 'Transaction Management',
  withdrawalManagement: 'Withdrawal Management',
  manualGameDepositManagement: 'Manual Game Deposit Management',
  ticketManagement: 'Ticket Management',
  adminManagement: 'Admin Management',
  agentManagement: 'Agent Management',
  agentPopupPermissionManagement: 'Agent Popup Permission Management',
  walletManagement: 'Wallet Management',
  gameProviderManagement: 'Game Provider Management',
  vfxConfigManagement: 'VFX Config Management',
  transactionLimitsManagement: 'Transaction Limits Management',
  domainManagement: 'Domain Management',
  cashSettlementManagement: 'Cash Settlement Management',
  cashSettlementVoid: 'Cash Settlement Void',
  reportManagement: 'Report Management',
};

// Fall back to the raw code if a new permission is added without a label yet.
export const getPermissionLabel = (code: string): string =>
  PERMISSION_LABELS[code as Permission] ?? code;

// Single display map for AdminRole. Use this everywhere (sidebar, header, Create Admin,
// tables). Do not invent "Manager" — labels follow the role matrix (QA-05).
export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  system_admin: 'System Admin',
  super_admin: 'Super Admin',
  platform_admin: 'Platform Admin',
  finance_admin: 'Finance Admin',
  operation_admin: 'Operation Admin',
  support_admin: 'Support Admin',
};

export const getAdminRoleLabel = (role?: string | null): string => {
  if (!role) return '';
  return ADMIN_ROLE_LABELS[role as AdminRole] ?? role.replace(/_/g, ' ');
};

export const ADMIN_ROLE_OPTIONS: Array<{ value: AdminRole; label: string }> = (
  Object.entries(ADMIN_ROLE_LABELS) as Array<[AdminRole, string]>
).map(([value, label]) => ({ value, label }));

// "Game manager" roles: scoped to specific game providers (wallet/transaction ops).
// Only finance_admin and operation_admin — mirrors hasCreateGameProviderRestriction in the
// Admin Management page. Used to decide whether to show the managed game providers section.
export const roleUsesGameProviders = (role?: AdminRole | null): boolean =>
  role === 'finance_admin' || role === 'operation_admin';

export type RolePermissionMap = Record<AdminRole, Permission[]>;

export const ROLE_PERMISSIONS: RolePermissionMap = {
  system_admin: ALL_PERMISSIONS,

  // super_admin: full operational access, no admin user management and no settlement void
  super_admin: ALL_PERMISSIONS.filter(
    (p) => !['adminManagement', 'cashSettlementVoid'].includes(p)
  ),

  finance_admin: [
    'dashboard',
    'walletManagement',
    'transactionManagement',
    'withdrawalManagement',
    'manualGameDepositManagement',
    'transactionLimitsManagement',
    'cashSettlementManagement',
    'reportManagement',
  ],

  // KIOSK-720: operation_admin — day-to-day operations, no finance actions.
  // cashSettlementManagement = View only Cash Settlement (update/void gated by role — operation excluded).
  operation_admin: [
    'dashboard',
    'userManagement',
    'agentManagement',
    'agentPopupPermissionManagement',
    'transactionManagement',
    'walletManagement',
    'ticketManagement',
    'gameProviderManagement',
    'cashSettlementManagement',
    'reportManagement',
  ],

  platform_admin: [
    'dashboard',
    'transactionManagement',
    'walletManagement',
    'reportManagement',
    'transactionLimitsManagement',
  ],

  support_admin: [
    'dashboard',
    'ticketManagement',
    'userManagement',
    'transactionManagement',
  ],
};

export const getRolePermissions = (role?: AdminRole | null): Permission[] => {
  if (!role) return [];
  return ROLE_PERMISSIONS[role] ?? [];
};

export const resolveUserPermissions = (user: AdminAccount | null): Permission[] => {
  if (!user) return [];

  // system_admin has all permissions unconditionally.
  // super_admin is intentionally NOT bypassed here (KIOSK-720): it must fall through
  // to ROLE_PERMISSIONS.super_admin below, which excludes adminManagement/cashSettlementVoid.
  const role = (user.role || '').toLowerCase();
  if (role === 'system_admin') {
    return ALL_PERMISSIONS;
  }

  // Prefer permissionObjects from API (Permission objects)
  if (Array.isArray(user.permissionObjects) && user.permissionObjects.length > 0) {
    return user.permissionObjects.map(p => p.code) as Permission[];
  }

  // Fallback to permissions array (string codes) — backward compatibility
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    return user.permissions as Permission[];
  }

  // Finally fallback to role permissions
  return getRolePermissions(user.role);
};

export const hasPageAccess = (
  user: AdminAccount | null,
  permission?: Permission,
): boolean => {
  if (!permission) return true;
  const permissions = resolveUserPermissions(user);
  return permissions.includes(permission);
};

export const matchesRole = (
  user: AdminAccount | null,
  targetRole: AdminRole | AdminRole[] | undefined,
): boolean => {
  if (!targetRole || !user) return true;
  if (Array.isArray(targetRole)) {
    return targetRole.includes(user.role);
  }
  return user.role === targetRole;
};
